import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Bookmark } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { HighlightCard } from "../components/dnh/highlights/HighlightCard";
import { TopicSearchResult } from "../components/dnh/search/TopicSearchResult";
import { LiveDiscoveryResults } from "../components/dnh/search/LiveDiscoveryResults";
import { KnowledgeResults } from "../components/dnh/search/KnowledgeResults";
import { SEARCH_INPUT_ID, focusDnhSearchInput } from "../components/dnh/search/SearchNavigation";
import { useAuth } from "../components/dnh/AuthContext";
import { apiFetch, getCategories, getCountries } from "../utils/api";
import { toast } from "sonner";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const { user } = useAuth();

  useEffect(() => { const frame = requestAnimationFrame(focusDnhSearchInput); return () => cancelAnimationFrame(frame); }, []);
  useEffect(() => {
    getCategories().then(d => setCategories(d.categories || [])).catch(() => {});
    getCountries().then(d => setCountries(d.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setInput(q); setData(null); setError(""); let active = true;
    if (!q.trim()) { setLoading(false); return; }
    const request = new URLSearchParams({
      q, page: params.get("page") || "1", limit: "20",
      date_range: params.get("date_range") || "all",
    });
    if (params.get("country"))  request.set("country", params.get("country"));
    if (params.get("category")) request.set("categories", params.get("category"));
    if (params.get("topic"))    request.set("topic", params.get("topic"));
    setLoading(true);
    apiFetch(`/api/search?${request}`)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError("Search is temporarily unavailable. This does not mean there is no coverage."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, q, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  const filter = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    if (key !== "page") next.delete("page");
    next.delete("research");
    setParams(next);
  };

  const submit = event => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    next.set("q", input.trim());
    next.delete("page"); next.delete("research");
    setParams(next);
  };

  const save = async () => {
    if (!user) { toast.info("Sign in to save searches"); return; }
    const saved = new URLSearchParams(params); saved.delete("research");
    try {
      await apiFetch("/api/user/saved-searches", {
        method: "POST",
        body: JSON.stringify({ query: q, label: q, kind: "text", url: `/search?${saved}` }),
      });
      toast.success("Search saved");
    } catch { toast.error("Search could not be saved"); }
  };

  /* Render a grid of HighlightCards with inline search feedback */
  const cards = (rows, partial = false) => (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      data-testid={partial ? "search-partial-results" : "search-live-results-list"}
    >
      {rows.map(story => (
        <div key={story.id} className="min-w-0 flex flex-col">
          {partial && (
            <p className="text-xs text-[var(--muted)] mb-2 leading-5" data-testid={`search-missing-${story.id}`}>
              Does not establish: {story.search_match?.missing?.join(", ") || "all requested details"}
            </p>
          )}
          <HighlightCard
            story={story}
            from={{ path: `/search?${params}`, label: "DNH Search" }}
            testIdPrefix={partial ? "search-partial-card" : "search-card"}
            searchFeedback={{ query: q, itemId: story.id, prefix: `search-feedback-${story.id}` }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <SiteHeader activeNav="Search" />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-4 md:pt-8 pb-10" data-testid="search-page">

        {/* ── Compact EditionShell-style header ── */}
        <header className="flex flex-wrap justify-between items-start gap-3 mb-5">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--crimson)] mb-1" data-testid="search-eyebrow">
              News · Evidence · Context
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight" data-testid="search-heading">
              DNH Search
            </h1>
          </div>
          {q && (
            <button
              data-testid="search-save"
              onClick={save}
              className="inline-flex gap-1.5 items-center h-9 px-3 rounded-full border border-[var(--hairline)] text-[12px] font-medium text-[var(--ink-2)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors whitespace-nowrap mt-1"
            >
              <Bookmark size={13} strokeWidth={1.75} />
              Save search
            </button>
          )}
        </header>

        {/* ── Search bar — compact, matching ScopedNewsSearch style ── */}
        <form onSubmit={submit} className="flex gap-2 mb-4 max-w-3xl" data-testid="search-form">
          <input
            id={SEARCH_INPUT_ID}
            data-testid="search-query-input"
            aria-label="Search DNH news"
            value={input}
            onChange={e => setInput(e.target.value)}
            maxLength={200}
            placeholder="Ask about a specific issue, person or event…"
            className="min-w-0 flex-1 bg-transparent border border-[var(--hairline)] rounded-lg px-4 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)] transition-colors"
          />
          <button
            data-testid="search-submit"
            aria-label="Search"
            className="bg-[var(--ink)] text-[var(--paper)] px-4 rounded-lg hover:bg-[var(--crimson)] transition-colors"
          >
            <SearchIcon size={18} />
          </button>
        </form>

        {/* ── Polished filter row — matching the scope bar style ── */}
        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-[var(--hairline)] items-center">
          <select
            data-testid="search-country-filter"
            aria-label="Country"
            value={params.get("country") || ""}
            onChange={e => filter("country", e.target.value)}
            className="dnh-select text-[13px]"
          >
            <option value="">All countries</option>
            {countries.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select
            data-testid="search-category-filter"
            aria-label="Category"
            value={params.get("category") || ""}
            onChange={e => filter("category", e.target.value)}
            className="dnh-select text-[13px]"
          >
            <option value="">All categories</option>
            {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select
            data-testid="search-date-filter"
            aria-label="Date range"
            value={params.get("date_range") || "all"}
            onChange={e => filter("date_range", e.target.value)}
            className="dnh-select text-[13px]"
          >
            <option value="all">All dates</option>
            <option value="today">Today</option>
            <option value="week">Past week</option>
            <option value="month">Past month</option>
          </select>
        </div>

        {/* ── Intent / search details ── */}
        {data?.intent && (
          <details className="mb-4 text-xs text-[var(--muted)]" data-testid="search-details">
            <summary className="cursor-pointer inline-flex items-center min-h-9" data-testid="search-details-toggle">
              Search details
            </summary>
            <p data-testid="search-required-concepts">Matching: {data.intent.terms.join(" + ") || "Add a subject or event"}</p>
            {data.intent.clarifications.map((note, i) => (
              <p className="border-l-2 border-[var(--crimson)] pl-3 mt-3" key={i} data-testid={`search-clarification-${i}`}>{note}</p>
            ))}
          </details>
        )}

        {/* ── Results ── */}
        {error ? (
          <div role="alert" className="py-10" data-testid="search-error">
            {error} <button data-testid="search-retry" onClick={() => setRetry(v => v + 1)} className="underline">Retry</button>
          </div>
        ) : loading ? (
          <p data-testid="search-loading" className="py-12 animate-pulse">Matching your complete question…</p>
        ) : !q ? (
          <p data-testid="search-prompt" className="py-12 text-[var(--muted)]">Search a topic, event or place.</p>
        ) : data && (
          <>
            {/* Direct matches section header */}
            <div className="flex justify-between items-center gap-4 mb-5">
              <h2 className="font-serif text-xl" data-testid="search-direct-heading">Direct news matches</h2>
              <p className="text-xs text-[var(--muted)]" data-testid="search-result-count">{data.total} matches · newest first</p>
            </div>

            {data.results.length
              ? cards(data.results)
              : <p data-testid="search-empty" className="py-6 border-y border-[var(--hairline)] text-sm">No exact news matches in DNH for this query and these filters. We haven't substituted unrelated reporting.</p>
            }

            {/* Pagination */}
            {data.total_pages > 1 && (
              <nav aria-label="Search result pages" className="flex flex-wrap gap-4 items-center my-6">
                <button className="dnh-outline-button disabled:opacity-40" data-testid="search-previous" disabled={data.page === 1} onClick={() => filter("page", String(data.page - 1))}>Previous</button>
                <span data-testid="search-page-count" className="text-xs">Page {data.page} of {data.total_pages}</span>
                <button className="dnh-outline-button disabled:opacity-40" data-testid="search-next" disabled={data.page >= data.total_pages} onClick={() => filter("page", String(data.page + 1))}>Next</button>
              </nav>
            )}

            {/* Live discovery */}
            {data.intent?.specific && (
              <LiveDiscoveryResults
                key={`live:${q}:${params.get("country")}:${params.get("date_range")}:${params.get("category")}:${params.get("topic")}`}
                query={q}
                params={params}
                indexed={data.results}
              />
            )}

            {/* Knowledge base */}
            {data.knowledge?.length > 0 && (
              <KnowledgeResults query={q} cards={data.knowledge} params={params} />
            )}

            {/* Partial coverage */}
            {!!data.related.length && (
              <section className="border-t border-[var(--hairline)] mt-10 pt-7">
                <h2 className="font-serif text-xl" data-testid="search-partial-heading">Partial coverage</h2>
                <p className="text-sm text-[var(--muted)] mt-1 mb-5">These reports leave some requested details unestablished.</p>
                {cards(data.related, true)}
              </section>
            )}

            {/* Topic research */}
            {q.trim().length >= 2 && data.intent?.specific && (
              <TopicSearchResult
                key={`topic:${q}:${params.get("country")}:${params.get("date_range")}:${params.get("category")}:${params.get("topic")}`}
                query={q}
                params={params}
              />
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
